import React, { useState } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Input, InputGroup, InputLeftElement, VStack, HStack, Box, Text, Button, Badge,
  Select, Spinner, Center, useToast, FormControl, FormLabel, Textarea,
} from "@chakra-ui/react";
import { Search } from "lucide-react";
import { buscarCrmClientes, criarCrmTarefa } from "../../services/api";
import { formatDoc, formatPhone, PRIORIDADES } from "./crmUtils";

const ACCENT = "#F68633";

/**
 * Cria uma tarefa e a atribui a um responsável (V1.5). Qualquer usuário do CRM pode
 * criar e escolher o responsável; sem escolha, a tarefa fica para o próprio usuário.
 * A tarefa é sempre vinculada a um cliente (busca obrigatória).
 */
export default function NovaTarefaModal({ isOpen, onClose, onCreated, vendedores = [] }) {
  const [q, setQ] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [cliente, setCliente] = useState(null);
  const [vendedorId, setVendedorId] = useState("");
  const [motivo, setMotivo] = useState("");
  const [prazo, setPrazo] = useState("");
  const [prioridade, setPrioridade] = useState("1");
  const [salvando, setSalvando] = useState(false);
  const toast = useToast();

  const reset = () => {
    setQ(""); setResultados([]); setCliente(null); setVendedorId("");
    setMotivo(""); setPrazo(""); setPrioridade("1");
  };
  const fechar = () => { reset(); onClose?.(); };

  const buscar = async (e) => {
    e?.preventDefault?.();
    if (q.trim().length < 2) return;
    setBuscando(true);
    const r = await buscarCrmClientes(q.trim());
    setBuscando(false);
    setResultados(r?.data || []);
  };

  const criar = async () => {
    if (!cliente || !motivo.trim()) { toast({ status: "warning", title: "Selecione um cliente e informe o motivo" }); return; }
    setSalvando(true);
    const r = await criarCrmTarefa({
      cliente_id: cliente.id,
      motivo: motivo.trim(),
      prazo: prazo || null,
      prioridade: Number(prioridade) || 0,
      vendedor_user_id: vendedorId ? Number(vendedorId) : undefined,
    });
    setSalvando(false);
    if (r?.data) { toast({ status: "success", title: "Tarefa criada" }); onCreated?.(); fechar(); }
    else toast({ status: "error", title: "Erro", description: r?.error });
  };

  return (
    <Modal isOpen={isOpen} onClose={fechar} size="lg" isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">Nova tarefa</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!cliente ? (
            <>
              <form onSubmit={buscar}>
                <HStack mb={3}>
                  <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none"><Search size={15} color="#999" /></InputLeftElement>
                    <Input placeholder="Buscar cliente por nome, documento ou telefone..." value={q} onChange={(e) => setQ(e.target.value)} />
                  </InputGroup>
                  <Button size="sm" type="submit" colorScheme="orange" isLoading={buscando}>Buscar</Button>
                </HStack>
              </form>
              {buscando ? (
                <Center py={8}><Spinner color={ACCENT} /></Center>
              ) : (
                <VStack align="stretch" spacing={2}>
                  {resultados.length === 0 && <Text fontSize="sm" color="gray.400" textAlign="center" py={6}>Digite e busque um cliente para a tarefa.</Text>}
                  {resultados.map((c) => (
                    <Box key={c.id} borderWidth="1px" borderColor="gray.100" borderRadius="lg" p={3} cursor="pointer" _hover={{ borderColor: ACCENT }} onClick={() => setCliente(c)}>
                      <HStack justify="space-between">
                        <Box minW={0}>
                          <HStack>
                            <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{c.nome || "Sem nome"}</Text>
                            {c.tipo_doc && <Badge colorScheme={c.tipo_doc === "CNPJ" ? "purple" : "blue"}>{c.tipo_doc === "CNPJ" ? "PJ" : "PF"}</Badge>}
                          </HStack>
                          <Text fontSize="xs" color="gray.500" noOfLines={1}>
                            {c.documento ? formatDoc(c.documento) : "sem documento"} · {c.telefone ? formatPhone(c.telefone) : "sem telefone"}
                          </Text>
                        </Box>
                        <Button size="xs" colorScheme="orange" flexShrink={0}>Selecionar</Button>
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              )}
            </>
          ) : (
            <VStack align="stretch" spacing={3}>
              <Box bg="orange.50" borderRadius="md" px={3} py={2}>
                <HStack justify="space-between">
                  <Box minW={0}>
                    <Text fontSize="sm" fontWeight="semibold" noOfLines={1}>{cliente.nome || "Sem nome"}</Text>
                    <Text fontSize="xs" color="gray.500" noOfLines={1}>{cliente.telefone ? formatPhone(cliente.telefone) : "sem telefone"}</Text>
                  </Box>
                  <Button size="xs" variant="ghost" onClick={() => setCliente(null)}>Trocar</Button>
                </HStack>
              </Box>
              <FormControl isRequired>
                <FormLabel fontSize="xs" mb={1}>Motivo</FormLabel>
                <Textarea size="sm" rows={2} placeholder="O que precisa ser feito?" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
              </FormControl>
              <HStack align="end" spacing={2}>
                <FormControl>
                  <FormLabel fontSize="xs" mb={1}>Responsável</FormLabel>
                  <Select size="sm" placeholder="Eu mesmo" value={vendedorId} onChange={(e) => setVendedorId(e.target.value)}>
                    {vendedores.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                  </Select>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="xs" mb={1}>Prazo</FormLabel>
                  <Input size="sm" type="date" value={prazo} onChange={(e) => setPrazo(e.target.value)} />
                </FormControl>
                <FormControl maxW="130px">
                  <FormLabel fontSize="xs" mb={1}>Prioridade</FormLabel>
                  <Select size="sm" value={prioridade} onChange={(e) => setPrioridade(e.target.value)}>
                    {PRIORIDADES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </Select>
                </FormControl>
              </HStack>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" mr={2} onClick={fechar}>Cancelar</Button>
          <Button size="sm" colorScheme="orange" onClick={criar} isLoading={salvando} isDisabled={!cliente || !motivo.trim()}>Criar tarefa</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
